/**
 * "3 hours ago" — the story shelf's and the leaderboard's only formatting need.
 *
 * Deliberately **not** the in-game dateline. `features/story/dateline.ts` renders the world's
 * clock (*Day 6 · evening*), which is the DM's fiction; this renders the player's own, which
 * is the answer to *"which of these did I play last?"*. Two clocks, two formatters, and the
 * one thing that would be wrong is a shelf that dates a save with the day the character
 * thinks it is.
 *
 * `Intl.RelativeTimeFormat` rather than a table of strings: it is in every browser we target
 * and it makes the plurals somebody else's problem. **It is handed the app's locale, not the
 * browser's** — the two disagree the moment a player sets a language in settings, and a
 * German shelf reading "3 hours ago" under German headings is the kind of half-translation
 * that reads worse than no translation at all.
 */
import { intlLocale, type Translate } from '@/i18n/t';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// One formatter pair per locale. Constructing an `Intl` formatter is the expensive part and
// the shelf renders one per row; the set of locales is three.
const FORMATTERS = new Map<
  string,
  { relative: Intl.RelativeTimeFormat; date: Intl.DateTimeFormat }
>();

function formatters(locale: string) {
  const tag = intlLocale(locale);
  let pair = FORMATTERS.get(tag);
  if (!pair) {
    pair = {
      relative: new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }),
      date: new Intl.DateTimeFormat(tag, { year: 'numeric', month: 'short', day: 'numeric' }),
    };
    FORMATTERS.set(tag, pair);
  }
  return pair;
}

/**
 * Empty string for an unparseable or absent timestamp, because every caller renders this
 * beside something else — a row that reads "Vera · 24 chapters ·" with a gap is better than
 * one that reads "Invalid Date", and neither is worth a branch at the call site.
 */
export function relativeTime(
  t: Translate,
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!iso) return '';
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';

  const { relative, date } = formatters(t.locale);
  const elapsed = now - at;
  // A clock skew of a few seconds is common and "in 4 seconds" reads as a bug.
  if (elapsed < MINUTE) return t('ui.common.just_now');
  if (elapsed < HOUR) return relative.format(-Math.floor(elapsed / MINUTE), 'minute');
  if (elapsed < DAY) return relative.format(-Math.floor(elapsed / HOUR), 'hour');
  if (elapsed < 7 * DAY) return relative.format(-Math.floor(elapsed / DAY), 'day');

  return date.format(at);
}
